import { useEffect, useMemo, useState } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import HubDetailSheet from "../../components/cop/HubDetailSheet";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Boxes, HardHat, Users } from "lucide-react";

const HUB_COLORS = [
  { hex: "#ef4444", name: "Kırmızı" },
  { hex: "#f97316", name: "Turuncu" },
  { hex: "#eab308", name: "Sarı" },
  { hex: "#22c55e", name: "Yeşil" },
  { hex: "#3b82f6", name: "Mavi" },
  { hex: "#8b5cf6", name: "Mor" },
];

export default function CopHubs() {
  const [hubs, setHubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // hub | null
  const [name, setName] = useState("");
  const [color, setColor] = useState(HUB_COLORS[0].hex);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // hub id
  const [detailHubId, setDetailHubId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/hubs");
      setHubs(data || []);
    } catch {
      toast.error("Hub'lar yüklenemedi");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const usedColors = useMemo(
    () => new Set(hubs.filter((h) => h.id !== editing?.id).map((h) => h.color)),
    [hubs, editing],
  );

  const openCreate = () => {
    setEditing(null);
    setName("");
    const free = HUB_COLORS.find((c) => !usedColors.has(c.hex));
    setColor((free || HUB_COLORS[0]).hex);
    setModalOpen(true);
  };
  const openEdit = (h) => {
    setEditing(h);
    setName(h.name);
    setColor(h.color);
    setModalOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Hub adı zorunludur");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/hubs/${editing.id}`, { name: name.trim(), color });
        toast.success("Hub güncellendi");
      } else {
        await api.post("/hubs", { name: name.trim(), color });
        toast.success("Hub eklendi");
      }
      setModalOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (h) => {
    try {
      await api.delete(`/hubs/${h.id}`);
      toast.success("Hub silindi");
      setConfirmDelete(null);
      load();
    } catch {
      toast.error("Hub silinemedi");
    }
  };

  return (
    <div className="space-y-6" data-testid="cop-hubs-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-500">
            COP31
          </div>
          <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight text-zinc-950 sm:text-4xl">
            Hub Yönetimi
          </h1>
          <p className="mt-1 text-sm text-zinc-500">
            Stand sayıları İnşaat Takip kayıtlarından otomatik hesaplanır. Detay
            için karta tıklayın.
          </p>
        </div>
        <Button
          onClick={openCreate}
          data-testid="hub-add-btn"
          className="gap-2 bg-zinc-900 text-white hover:bg-zinc-800"
        >
          <Plus className="h-4 w-4" />
          Hub Ekle
        </Button>
      </div>

      {loading ? (
        <div className="py-16 text-center text-sm text-zinc-400">Yükleniyor…</div>
      ) : hubs.length === 0 ? (
        <div className="rounded-xl border border-dashed border-zinc-300 py-16 text-center">
          <Boxes className="mx-auto h-8 w-8 text-zinc-300" />
          <p className="mt-2 text-sm text-zinc-500">
            Henüz hub yok — sağ üstten "Hub Ekle" ile başlayın.
          </p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {hubs.map((h) => (
            <div
              key={h.id}
              data-testid={`hub-card-${h.id}`}
              onClick={() => setDetailHubId(h.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && setDetailHubId(h.id)}
              className="group relative cursor-pointer overflow-hidden rounded-xl p-5 text-white shadow-sm transition-transform hover:-translate-y-0.5"
              style={{ backgroundColor: h.color }}
            >
              <div className="text-lg font-semibold leading-tight">{h.name}</div>
              <div className="mt-3 flex items-center gap-4 text-sm font-medium text-white/90">
                <span className="flex items-center gap-1.5">
                  <HardHat className="h-4 w-4" />
                  <span data-testid={`hub-stand-count-${h.id}`}>
                    {h.stand_count} Stand
                  </span>
                </span>
                <span className="flex items-center gap-1.5">
                  <Users className="h-4 w-4" />
                  <span data-testid={`hub-staff-count-${h.id}`}>
                    {h.staff_count || 0} Görevli
                  </span>
                </span>
              </div>
              <div className="mt-0.5 text-[11px] text-white/70">
                İnşaat Takip'ten otomatik
              </div>
              <div className="absolute right-2 top-2 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    openEdit(h);
                  }}
                  data-testid={`hub-edit-${h.id}`}
                  title="Düzenle"
                  className="flex h-7 w-7 items-center justify-center rounded-md bg-white/20 text-white hover:bg-white/30"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                {confirmDelete === h.id ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      remove(h);
                    }}
                    data-testid={`hub-delete-confirm-${h.id}`}
                    className="flex h-7 items-center rounded-md bg-white px-2 text-[11px] font-semibold text-red-600 hover:bg-red-50"
                  >
                    Sil?
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setConfirmDelete(h.id);
                    }}
                    data-testid={`hub-delete-${h.id}`}
                    title="Sil"
                    className="flex h-7 w-7 items-center justify-center rounded-md bg-white/20 text-white hover:bg-white/30"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <HubDetailSheet
        hubId={detailHubId}
        open={!!detailHubId}
        onClose={() => setDetailHubId(null)}
        onChanged={load}
      />

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-sm" data-testid="hub-modal">
          <DialogHeader>
            <DialogTitle>{editing ? "Hub Düzenle" : "Hub Ekle"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">
                Renk
              </Label>
              <div className="mt-2 flex gap-2">
                {HUB_COLORS.map((c) => {
                  const used = usedColors.has(c.hex);
                  return (
                    <button
                      key={c.hex}
                      type="button"
                      onClick={() => setColor(c.hex)}
                      data-testid={`hub-color-${c.hex.slice(1)}`}
                      title={used ? `${c.name} (kullanımda)` : c.name}
                      className={[
                        "relative h-9 w-9 rounded-full transition-transform hover:scale-110",
                        color === c.hex
                          ? "ring-2 ring-zinc-900 ring-offset-2"
                          : "ring-1 ring-zinc-200",
                        used && color !== c.hex ? "opacity-40" : "",
                      ].join(" ")}
                      style={{ backgroundColor: c.hex }}
                    >
                      {used && (
                        <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border border-white bg-zinc-500" />
                      )}
                    </button>
                  );
                })}
              </div>
              <p className="mt-1 text-[11px] text-zinc-400">
                Soluk renkler başka bir hub'da kullanılıyor.
              </p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">
                Hub Adı
              </Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="örn. Technology & Innovation"
                className="mt-1"
                maxLength={80}
                data-testid="hub-name-input"
              />
            </div>
            <div className="rounded-md bg-zinc-50 px-3 py-2">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                Stand Sayısı
              </div>
              <div className="mt-0.5 text-sm text-zinc-700" data-testid="hub-modal-stand-count">
                {editing
                  ? `${editing.stand_count ?? 0} Stand — İnşaat Takip kayıtlarından otomatik`
                  : "İnşaat Takip'te bu hub adıyla eşleşen kayıtlardan otomatik hesaplanır"}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setModalOpen(false)}
              >
                İptal
              </Button>
              <Button
                type="submit"
                disabled={saving || !name.trim()}
                data-testid="hub-save-btn"
                className="bg-zinc-900 text-white hover:bg-zinc-800"
              >
                Kaydet
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
